/**
 * @draftedge/api-client — typed Laravel API client.
 *
 * USAGE:
 *   import { createApiClient, createLocalStorageTokenStore } from '@draftedge/api-client';
 *   const api = createApiClient({
 *     baseUrl: '/v2',
 *     tokenStore: createLocalStorageTokenStore(),
 *   });
 *   const { user, token } = await api.auth.login({ login: 'me@x.com', password: 's3cret' });
 *
 * Phase 2 endpoints are wired against the live Laravel routes (verified
 * 2026-04-25):
 *   - /v2/auth/*         — sessions, registration, password reset, OAuth
 *   - /v2/account/*      — profile read/write, subscription cancellation
 *   - /v2/tokens/*       — in-app currency balance + deduction
 *   - /v2/payments/*     — Stripe + PayPal checkout sessions
 *
 * Phase 3+ domains (players, sport dashboards, projections) still resolve to
 * `{ todo: true }` until their Laravel controllers ship.
 *
 * Auth uses bearer tokens (Laravel Sanctum). The token is stored by the SPA —
 * on web in `localStorage`, on Capacitor in `@capacitor/preferences`. The
 * `tokenStore` abstraction (see `./tokenStore`) keeps this client framework-
 * agnostic so the same code path works for browser + iOS + Android WebViews.
 */
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  AxiosError,
} from 'axios';
import {
  ApiErrorSchema,
  type ApiError,
  type AppendAdviceRequest,
  type AuthResponse,
  type CancelSubscriptionResponse,
  type CheatsheetResponse,
  type CheckoutRequest,
  type CheckoutResponse,
  type CreateDraftRequest,
  type DeleteDraftResponse,
  type DraftListResponse,
  type DraftResponse,
  type LoginRequest,
  type MeResponse,
  type MembershipPlansResponse,
  type NewsArticleResponse,
  type NewsListResponse,
  type NflPlayer,
  type PaypalCheckoutRequest,
  type PaypalCheckoutResponse,
  type PlayerListQuery,
  type PlayerListResponse,
  type PlayerPreview,
  type PlayerPreviewListResponse,
  type TeamScheduleResponse,
  type ProfileResponse,
  type ProjectionsResponse,
  type RankingsBlob,
  type RankingsResponse,
  type RegisterRequest,
  type RegisterResponse,
  type SimpleSuccess,
  type Sport,
  type SportCode,
  type SportDashboard,
  type SportDashboardResponse,
  type SportInjuriesResponse,
  type SportPowerRankingsResponse,
  type SportProjectionsResponse,
  type SportScheduleResponse,
  type TodoResponse,
  type TokenBalance,
  type TokenDeductRequest,
  type TokenDeductResponse,
  type TrendingResponse,
  type UpdateDraftRequest,
  type UpdateProfileRequest,
} from './types';
import {
  createMemoryTokenStore,
  type TokenStore,
} from './tokenStore';

export * from './types';
export * from './tokenStore';

// ---------- Public client surface ----------------------------------------

export interface ApiClientOptions {
  baseUrl: string;
  tokenStore?: TokenStore;
  /** Optional default headers — useful for `X-Client-Version` etc. */
  headers?: Record<string, string>;
  /** Request timeout in milliseconds. */
  timeoutMs?: number;
  /** Hook called whenever a 401 is observed — SPA should redirect to /login. */
  onUnauthorized?: () => void;
}

export interface ApiClient {
  raw: AxiosInstance;
  tokenStore: TokenStore;
  setAuthToken: (token: string | null) => Promise<void>;
  auth: {
    login: (req: LoginRequest) => Promise<AuthResponse>;
    /** Cross-domain SSO from draftedge.com — exchange a signed handoff token
     * for a Sanctum session. POST /v2/auth/sso → same shape as login. */
    sso: (token: string) => Promise<AuthResponse>;
    register: (req: RegisterRequest) => Promise<RegisterResponse>;
    verify: (token: string) => Promise<SimpleSuccess>;
    resendVerification: (email: string) => Promise<SimpleSuccess>;
    requestPasswordReset: (email: string) => Promise<SimpleSuccess>;
    resetPassword: (req: {
      token: string;
      password: string;
    }) => Promise<SimpleSuccess>;
    me: () => Promise<MeResponse>;
    logout: () => Promise<SimpleSuccess>;
    /** Returns the absolute URL the browser should hit to start Google OAuth. */
    googleRedirectUrl: () => string;
  };
  account: {
    getProfile: () => Promise<ProfileResponse>;
    updateProfile: (patch: UpdateProfileRequest) => Promise<ProfileResponse>;
    cancelSubscription: (
      reason?: string,
    ) => Promise<CancelSubscriptionResponse>;
  };
  payments: {
    stripeCheckout: (req: CheckoutRequest) => Promise<CheckoutResponse>;
    paypalCheckout: (
      req: PaypalCheckoutRequest,
    ) => Promise<PaypalCheckoutResponse>;
    /** Fetch the live membership plans catalog (replaces hardcoded SPA list). */
    listPlans: () => Promise<MembershipPlansResponse>;
  };
  tokens: {
    balance: () => Promise<TokenBalance>;
    deduct: (req: TokenDeductRequest) => Promise<TokenDeductResponse>;
    history: () => Promise<TodoResponse>;
  };
  players: {
    list: (
      q: PlayerListQuery,
    ) => Promise<PlayerListResponse | TodoResponse>;
    get: (id: number) => Promise<TodoResponse>;
  };
  /**
   * Sport dashboards / projections / schedule / injuries / power rankings.
   * All public (no auth). Uses {@link SportCode} for the route param.
   */
  sport: {
    /** Legacy stub — accepts the union type; real callers should use `SportCode`. */
    dashboard: (
      sport: SportCode | Sport,
    ) => Promise<SportDashboardResponse | SportDashboard | TodoResponse>;
    projections: (
      sport: SportCode | Sport,
      params?: SportProjectionsParams,
    ) => Promise<SportProjectionsResponse | ProjectionsResponse | TodoResponse>;
    schedule: (sport: SportCode | Sport) => Promise<SportScheduleResponse>;
    injuries: (sport: SportCode | Sport) => Promise<SportInjuriesResponse>;
    powerRankings: (
      sport: SportCode | Sport,
    ) => Promise<SportPowerRankingsResponse>;
    /** @deprecated kept for older callers; prefer `powerRankings`. */
    rankings: (sport: SportCode | Sport) => Promise<TodoResponse>;
  };
  /** GET /v2/news + GET /v2/news/{id} — public, no auth required. */
  news: {
    list: (params?: NewsListParams) => Promise<NewsListResponse>;
    show: (id: number | string) => Promise<NewsArticleResponse>;
  };
  /**
   * NFL draft assistant CRUD. All endpoints require auth (Bearer token).
   *
   * Endpoint shapes mirror the legacy save_league.php / get_leagues.php /
   * delete-league.php / get_draft_data.php trio that the parallel Laravel
   * agent is consolidating under /v2/drafts/*. See `DraftSummary` / `Draft`
   * in `./types` for the canonical row shapes.
   */
  drafts: {
    list: () => Promise<DraftListResponse>;
    show: (id: number | string) => Promise<DraftResponse>;
    create: (payload: CreateDraftRequest) => Promise<DraftResponse>;
    update: (
      id: number | string,
      payload: UpdateDraftRequest,
    ) => Promise<DraftResponse>;
    delete: (id: number | string) => Promise<DeleteDraftResponse>;
    /** Append a single coach interaction to the `aiAdvice` JSON column. */
    appendAdvice: (
      id: number | string,
      advice: AppendAdviceRequest,
    ) => Promise<DraftResponse>;
  };
  /**
   * Custom rankings / cheat-sheet (Phase 4.1) — GET/PUT/DELETE
   * /v2/me/rankings. One SPA-owned blob per user; requires auth.
   */
  rankings: {
    get: () => Promise<RankingsResponse>;
    save: (rankings: RankingsBlob) => Promise<RankingsResponse>;
    reset: () => Promise<SimpleSuccess>;
  };
  /**
   * NFL-specific endpoints sourced from the JSON files we already maintain
   * via manual upload (file.json, trending1.json, player_previews.json).
   * Distinct from the generic `sport` namespace — these are NFL-only and
   * return richer per-sport shapes.
   */
  nfl: {
    players: {
      /** GET /v2/nfl/players — paginated cheatsheet (file.json / limited.json). */
      list: (params?: NflPlayerListParams) => Promise<CheatsheetResponse>;
      /** GET /v2/nfl/players/{id} — single player detail (cross-references). */
      show: (id: number | string) => Promise<NflPlayer>;
    };
    /** GET /v2/nfl/trending — top movers, weekly leaders, week number. */
    trending: () => Promise<TrendingResponse>;
    /**
     * GET /v2/nfl/player-previews — narrative scouting reports.
     *
     * Callable for backward compatibility (`api.nfl.playerPreviews({limit})`).
     * Also exposes `.list(params)` and `.show(playerId)` for new callers — the
     * single-player accessor was added in Round 3 to power /players/:id +
     * draft sidebar Scouting Report surfaces without fetching the full list.
     */
    playerPreviews: PlayerPreviewsAccessor;
    /**
     * GET /v2/nfl/team-schedule/{team} — per-team upcoming schedule with
     * opposing defense ranks vs each fantasy position. Powers the player
     * detail "Schedule" card in the draft tool sidebar.
     */
    teamSchedule: (team: string) => Promise<TeamScheduleResponse>;
  };
  /**
   * AI Draft Coach + related token-gated AI surfaces.
   *
   * Streaming endpoints aren't wrapped (axios doesn't speak SSE cleanly) —
   * `draftAdviceStreamUrl(id)` returns the absolute URL the consumer should
   * `fetch()` with their own ReadableStream parser. The bearer is attached
   * separately (the SSE hook reads from `tokenStore.get()`).
   */
  ai: {
    /** Absolute URL for POST /v2/drafts/{id}/advice/stream (SSE). */
    draftAdviceStreamUrl: (draftId: number | string) => string;
  };
}

/**
 * Callable type that doubles as an object with named methods. Lets the legacy
 * `api.nfl.playerPreviews({limit})` invocation keep working while exposing
 * `api.nfl.playerPreviews.show(playerId)` for the single-player accessor.
 */
export type PlayerPreviewsAccessor = ((params?: {
  limit?: number;
}) => Promise<PlayerPreviewListResponse>) & {
  list: (params?: { limit?: number }) => Promise<PlayerPreviewListResponse>;
  show: (playerId: number | string) => Promise<PlayerPreview | null>;
};

/**
 * Query params accepted by GET /v2/{sport}/projections. All optional — server
 * defaults to page=1, per_page=50.
 */
export interface SportProjectionsParams {
  /** Comma-separated list (e.g. "QB,RB") or a single position. */
  position?: string | string[];
  team?: string;
  page?: number;
  limit?: number;
}

/** Query params accepted by GET /v2/news. */
export interface NewsListParams {
  /** Sport filter — case-insensitive on the server. */
  sport?: string;
  page?: number;
  limit?: number;
}

/**
 * Query params accepted by GET /v2/nfl/players. All optional — server
 * defaults to page=1, per_page=25, sort by ADP ascending.
 */
export interface NflPlayerListParams {
  /** Comma-separated list (e.g. "QB,RB") or a single position. */
  position?: string | string[];
  team?: string;
  /** Free-text search against player name. */
  q?: string;
  page?: number;
  limit?: number;
  /** Sort key — `adp` | `rank` | `proj` | `name`. Server validates. */
  sort?: string;
}

// ---------- Implementation ------------------------------------------------

export function createApiClient(options: ApiClientOptions): ApiClient {
  const tokenStore = options.tokenStore ?? createMemoryTokenStore();

  const instance = axios.create({
    baseURL: options.baseUrl,
    timeout: options.timeoutMs ?? 15000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    },
    // Cookie-based fallback for Sanctum if the SPA + API share an origin.
    withCredentials: true,
  });

  instance.interceptors.request.use(async (config) => {
    const token = await tokenStore.get();
    if (token) {
      config.headers.set?.('Authorization', `Bearer ${token}`);
    }
    return config;
  });

  instance.interceptors.response.use(
    (resp) => resp,
    async (error: AxiosError) => {
      if (error.response?.status === 401) {
        // Token is no good — purge it so the next request is anonymous and
        // the SPA can react via `onUnauthorized`.
        await tokenStore.clear();
        options.onUnauthorized?.();
      }
      return Promise.reject(normalizeError(error));
    },
  );

  // Stub helper for endpoints whose Laravel controllers don't exist yet.
  const todo = async (): Promise<TodoResponse> => ({ todo: true });

  return {
    raw: instance,
    tokenStore,

    setAuthToken: async (token) => {
      if (token === null) {
        await tokenStore.clear();
      } else {
        await tokenStore.set(token);
      }
    },

    auth: {
      // POST /v2/auth/login → { success, message, user, token, token_type }
      login: async (req) => {
        const body = { token: 1 as const, ...req };
        const { data } = await instance.post<AuthResponse>('/auth/login', body);
        if (data?.token) {
          await tokenStore.set(data.token);
        }
        return data;
      },

      // POST /v2/auth/sso → { success, message, user, token, token_type }
      // Consumes the HMAC handoff token minted by draftedge.com.
      sso: async (token) => {
        const { data } = await instance.post<AuthResponse>('/auth/sso', {
          token,
        });
        if (data?.token) {
          await tokenStore.set(data.token);
        }
        return data;
      },

      // POST /v2/auth/register → { success, message, user_id }
      // Does NOT auto-login — user must verify email first.
      register: async (req) => {
        const { data } = await instance.post<RegisterResponse>(
          '/auth/register',
          req,
        );
        return data;
      },

      // GET /v2/auth/verify/{token} → { success, message }
      verify: async (token) => {
        const { data } = await instance.get<SimpleSuccess>(
          `/auth/verify/${encodeURIComponent(token)}`,
        );
        return data;
      },

      // POST /v2/auth/resend-verification → { success, message }
      resendVerification: async (email) => {
        const { data } = await instance.post<SimpleSuccess>(
          '/auth/resend-verification',
          { email },
        );
        return data;
      },

      // POST /v2/auth/password/forgot → { success, message }
      requestPasswordReset: async (email) => {
        const { data } = await instance.post<SimpleSuccess>(
          '/auth/password/forgot',
          { email },
        );
        return data;
      },

      // POST /v2/auth/password/reset → { success, message }
      resetPassword: async (req) => {
        const { data } = await instance.post<SimpleSuccess>(
          '/auth/password/reset',
          req,
        );
        return data;
      },

      // GET /v2/auth/me (Bearer) → { success, user, tokens }
      me: async () => {
        const { data } = await instance.get<MeResponse>('/auth/me');
        return data;
      },

      // POST /v2/auth/logout (Bearer) → { success, message }
      logout: async () => {
        try {
          const { data } = await instance.post<SimpleSuccess>('/auth/logout');
          return data;
        } finally {
          // Always clear locally — if the server 401'd we still want the
          // client to forget the token.
          await tokenStore.clear();
        }
      },

      googleRedirectUrl: () => {
        // Anchor the redirect to the same baseURL so prod & dev both work.
        const base = options.baseUrl.replace(/\/+$/, '');
        return `${base}/auth/google/redirect`;
      },
    },

    account: {
      // GET /v2/account/profile (Bearer)
      //   → { success, user, profile, subscription, tokens }
      getProfile: async () => {
        const { data } = await instance.get<ProfileResponse>(
          '/account/profile',
        );
        return data;
      },

      // PUT /v2/account/profile (Bearer)
      //   body: { first_name, last_name, phone?, bio?, avatar_url?, timezone? }
      //   → { success, user, profile, subscription, tokens }
      updateProfile: async (patch) => {
        const { data } = await instance.put<ProfileResponse>(
          '/account/profile',
          patch,
        );
        return data;
      },

      // POST /v2/account/cancel-subscription (Bearer)
      //   body: { reason? }
      //   → { success, subscription: {status:'cancelling', ...} }
      cancelSubscription: async (reason) => {
        const { data } = await instance.post<CancelSubscriptionResponse>(
          '/account/cancel-subscription',
          reason ? { reason } : {},
        );
        return data;
      },
    },

    payments: {
      // POST /v2/payments/stripe/checkout (Bearer)
      //   body: { plan_slug, success_url, cancel_url }
      //   → { success, checkout_url, session_id }   (502 if no Stripe key)
      stripeCheckout: async (req) => {
        const { data } = await instance.post<CheckoutResponse>(
          '/payments/stripe/checkout',
          req,
        );
        return data;
      },

      // POST /v2/payments/paypal/checkout (Bearer)
      //   body: { plan_slug, return_url, cancel_url }
      //   → { success, approve_url }   (502 if no PayPal key)
      paypalCheckout: async (req) => {
        const { data } = await instance.post<PaypalCheckoutResponse>(
          '/payments/paypal/checkout',
          req,
        );
        return data;
      },

      // GET /v2/membership-plans → { plans: [...] }
      // Public; the existing bearer interceptor is harmless.
      listPlans: async () => {
        const { data } = await instance.get<MembershipPlansResponse>(
          '/membership-plans',
        );
        return data;
      },
    },

    tokens: {
      // GET /v2/tokens/balance (Bearer) → rich balance + monthly entitlement
      balance: async () => {
        const { data } = await instance.get<TokenBalance>('/tokens/balance');
        return data;
      },

      // POST /v2/tokens/deduct (Bearer)
      //   body: { amount: 1-1000, feature: enum }
      //   → { success, deducted, new_balance, transaction_id }   (402 insufficient)
      deduct: async (req) => {
        const { data } = await instance.post<TokenDeductResponse>(
          '/tokens/deduct',
          req,
        );
        return data;
      },

      history: () => todo(),
    },

    players: {
      list: (_q) => todo(),
      get: (_id) => todo(),
    },

    sport: {
      // GET /v2/{sport}/dashboard — JSON dashboard, ETag + 5min cache server-side.
      dashboard: async (sport) => {
        const { data } = await instance.get<SportDashboardResponse>(
          `/${sport}/dashboard`,
        );
        return data;
      },

      // GET /v2/{sport}/projections?position=QB,RB&team=BAL&limit=50&page=1
      projections: async (sport, params) => {
        const { data } = await instance.get<SportProjectionsResponse>(
          `/${sport}/projections`,
          { params: serializeProjectionsParams(params) },
        );
        return data;
      },

      // GET /v2/{sport}/schedule
      schedule: async (sport) => {
        const { data } = await instance.get<SportScheduleResponse>(
          `/${sport}/schedule`,
        );
        return data;
      },

      // GET /v2/{sport}/injuries
      injuries: async (sport) => {
        const { data } = await instance.get<SportInjuriesResponse>(
          `/${sport}/injuries`,
        );
        return data;
      },

      // GET /v2/{sport}/powerrankings
      powerRankings: async (sport) => {
        const { data } = await instance.get<SportPowerRankingsResponse>(
          `/${sport}/powerrankings`,
        );
        return data;
      },

      // Legacy alias
      rankings: (_sport) => todo(),
    },

    news: {
      // GET /v2/news?sport=NFL&limit=20&page=1
      list: async (params) => {
        const { data } = await instance.get<NewsListResponse>('/news', {
          params: serializeNewsParams(params),
        });
        return data;
      },

      // GET /v2/news/{id}
      show: async (id) => {
        const { data } = await instance.get<NewsArticleResponse>(
          `/news/${encodeURIComponent(String(id))}`,
        );
        return data;
      },
    },

    drafts: {
      // GET /v2/drafts (Bearer) → { data: DraftSummary[] } | DraftSummary[]
      list: async () => {
        const { data } = await instance.get<DraftListResponse>('/drafts');
        return data;
      },

      // GET /v2/drafts/{id} (Bearer) → { success, data: Draft } | Draft
      show: async (id) => {
        const { data } = await instance.get<DraftResponse>(
          `/drafts/${encodeURIComponent(String(id))}`,
        );
        return data;
      },

      // POST /v2/drafts (Bearer) body: CreateDraftRequest → { success, data: Draft }
      create: async (payload) => {
        const { data } = await instance.post<DraftResponse>(
          '/drafts',
          payload,
        );
        return data;
      },

      // PUT /v2/drafts/{id} (Bearer) body: UpdateDraftRequest → { success, data: Draft }
      update: async (id, payload) => {
        const { data } = await instance.put<DraftResponse>(
          `/drafts/${encodeURIComponent(String(id))}`,
          payload,
        );
        return data;
      },

      // DELETE /v2/drafts/{id} (Bearer) → { success, message? }
      delete: async (id) => {
        const { data } = await instance.delete<DeleteDraftResponse>(
          `/drafts/${encodeURIComponent(String(id))}`,
        );
        return data;
      },

      // POST /v2/drafts/{id}/advice (Bearer) body: AppendAdviceRequest
      //   → { success, data: Draft } (server appends to aiAdvice JSON column)
      appendAdvice: async (id, advice) => {
        const { data } = await instance.post<DraftResponse>(
          `/drafts/${encodeURIComponent(String(id))}/advice`,
          advice,
        );
        return data;
      },
    },

    rankings: {
      // GET /v2/me/rankings (Bearer) → { rankings: blob|null, updated_at }
      get: async () => {
        const { data } = await instance.get<RankingsResponse>('/me/rankings');
        return data;
      },

      // PUT /v2/me/rankings (Bearer) body: { rankings: blob } — upsert.
      save: async (rankings) => {
        const { data } = await instance.put<RankingsResponse>(
          '/me/rankings',
          { rankings },
        );
        return data;
      },

      // DELETE /v2/me/rankings (Bearer) — reset to default (ADP) order.
      reset: async () => {
        const { data } = await instance.delete<SimpleSuccess>('/me/rankings');
        return data;
      },
    },

    nfl: {
      players: {
        // GET /v2/nfl/players?position=QB,RB&team=BAL&q=&limit=25&page=1&sort=adp
        // Public-readable; bearer interceptor harmlessly attaches token if
        // present (server uses it to gate premium tier vs limited.json).
        list: async (params) => {
          const { data } = await instance.get<CheatsheetResponse>(
            '/nfl/players',
            { params: serializeNflPlayerListParams(params) },
          );
          return data;
        },

        // GET /v2/nfl/players/{id} — single record (cross-referenced from
        // file.json + players.json + player_previews.json + trending1.json).
        // Server wraps as {data, source, preview}; unwrap to the player record
        // so callers can use the same NflPlayer shape they get from list().
        show: async (id) => {
          const { data } = await instance.get<{ data: NflPlayer }>(
            `/nfl/players/${encodeURIComponent(String(id))}`,
          );
          return data.data;
        },
      },

      // GET /v2/nfl/trending → { week, leaders, gainers, fallers, projection_count }
      trending: async () => {
        const { data } = await instance.get<TrendingResponse>('/nfl/trending');
        return data;
      },

      // GET /v2/nfl/player-previews?limit=5
      // PlayerPreviewsAccessor: callable AND exposes .list/.show. Backward-
      // compatible with the legacy `api.nfl.playerPreviews({limit})` shape.
      playerPreviews: makePlayerPreviewsAccessor(instance),

      // GET /v2/nfl/team-schedule/{team} — public; returns {team, games[]}
      teamSchedule: async (team) => {
        const safe = encodeURIComponent(team.trim().toUpperCase());
        const { data } = await instance.get<TeamScheduleResponse>(
          `/nfl/team-schedule/${safe}`,
        );
        return data;
      },
    },

    ai: {
      // POST /v2/drafts/{id}/advice/stream — Server-Sent Events. Returns the
      // absolute URL only; SSE consumption lives in the SPA's
      // `useAiAdviceStream` hook (axios doesn't speak SSE; the hook uses a
      // fetch + ReadableStream reader with AbortController for cancellation).
      draftAdviceStreamUrl: (draftId) => {
        const base = options.baseUrl.replace(/\/+$/, '');
        return `${base}/drafts/${encodeURIComponent(String(draftId))}/advice/stream`;
      },
    },
  };
}

/**
 * Builds the callable+object hybrid for `nfl.playerPreviews`. Implementing
 * this as a standalone factory keeps the main `createApiClient` body readable
 * — the type gymnastics live here.
 *
 * The returned function preserves the legacy `(params?) => Promise<list>`
 * shape while attaching `.list` (alias) and `.show(id)` for the single-player
 * accessor. `.show` returns `null` on 404 so callers can render a graceful
 * empty state without a try/catch.
 */
function makePlayerPreviewsAccessor(
  instance: AxiosInstance,
): PlayerPreviewsAccessor {
  const list = async (params?: { limit?: number }) => {
    const { data } = await instance.get<PlayerPreviewListResponse>(
      '/nfl/player-previews',
      { params: params?.limit !== undefined ? { limit: params.limit } : {} },
    );
    return data;
  };

  const show = async (playerId: number | string): Promise<PlayerPreview | null> => {
    try {
      const { data } = await instance.get<PlayerPreview>(
        `/nfl/player-previews/${encodeURIComponent(String(playerId))}`,
      );
      // Server may wrap in {data: ...}; unwrap defensively. Cast through
      // unknown because PlayerPreview (Zod passthrough) and the wrapper
      // shape don't structurally overlap.
      const raw = data as unknown as Record<string, unknown>;
      if (
        raw &&
        typeof raw === 'object' &&
        'data' in raw &&
        raw.data &&
        typeof raw.data === 'object'
      ) {
        return raw.data as PlayerPreview;
      }
      // 200-with-null-preview = "no scouting report on file" (the server
      // stopped 404ing this expected state). Normalize to null for the UI.
      if (
        raw &&
        typeof raw === 'object' &&
        'preview' in raw &&
        (raw.preview === null || raw.preview === '')
      ) {
        return null;
      }
      return data;
    } catch (err) {
      // 404 → no preview on file for this player. Surface as null so the UI
      // can render an empty state instead of catching an exception. Any
      // other status (5xx, network) propagates so the consumer can show an
      // error toast. Axios attaches the HTTP status under `err.response.status`
      // (NOT `err.status`), so we check both shapes defensively.
      const e = err as { status?: number; response?: { status?: number } };
      const status = e?.response?.status ?? e?.status;
      if (status === 404) {
        return null;
      }
      throw err;
    }
  };

  const accessor = ((params?: { limit?: number }) => list(params)) as PlayerPreviewsAccessor;
  accessor.list = list;
  accessor.show = show;
  return accessor;
}

// ---------- Param serializers ------------------------------------------

function serializeProjectionsParams(
  params?: SportProjectionsParams,
): Record<string, string | number> {
  if (!params) return {};
  const out: Record<string, string | number> = {};
  if (params.position !== undefined) {
    out.position = Array.isArray(params.position)
      ? params.position.join(',')
      : params.position;
  }
  if (params.team !== undefined) out.team = params.team;
  if (params.page !== undefined) out.page = params.page;
  if (params.limit !== undefined) out.limit = params.limit;
  return out;
}

function serializeNewsParams(
  params?: NewsListParams,
): Record<string, string | number> {
  if (!params) return {};
  const out: Record<string, string | number> = {};
  if (params.sport !== undefined && params.sport !== '') out.sport = params.sport;
  if (params.page !== undefined) out.page = params.page;
  if (params.limit !== undefined) out.limit = params.limit;
  return out;
}

function serializeNflPlayerListParams(
  params?: NflPlayerListParams,
): Record<string, string | number> {
  if (!params) return {};
  const out: Record<string, string | number> = {};
  if (params.position !== undefined) {
    out.position = Array.isArray(params.position)
      ? params.position.join(',')
      : params.position;
  }
  if (params.team !== undefined && params.team !== '') out.team = params.team;
  if (params.q !== undefined && params.q !== '') out.q = params.q;
  if (params.page !== undefined) out.page = params.page;
  if (params.limit !== undefined) out.limit = params.limit;
  if (params.sort !== undefined && params.sort !== '') out.sort = params.sort;
  return out;
}


// ---------- Helpers -------------------------------------------------------

export interface NormalizedApiError extends Error {
  apiError?: ApiError;
  status?: number;
}

function normalizeError(error: AxiosError): NormalizedApiError {
  const wrapped = new Error(
    error.message ?? 'Unknown API error',
  ) as NormalizedApiError;
  wrapped.status = error.response?.status;
  const parsed = ApiErrorSchema.safeParse(error.response?.data);
  if (parsed.success) {
    wrapped.apiError = parsed.data;
    wrapped.message = parsed.data.message;
  } else if (
    typeof error.response?.data === 'object' &&
    error.response?.data !== null &&
    'message' in error.response.data &&
    typeof (error.response.data as { message: unknown }).message === 'string'
  ) {
    // Laravel sometimes returns `{ success: false, message: "..." }` without
    // matching the strict `ApiError` shape — surface that message anyway.
    wrapped.message = (error.response.data as { message: string }).message;
  }
  return wrapped;
}

// Re-export axios config type so callers can layer on extra options.
export type { AxiosRequestConfig };
